import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import statesArray from '@wb/../assets/json/states.json';
import { Criteria } from '@wb/core/interfaces/criteria';
import { Model } from '@wb/core/interfaces/inventory';
import {
  CreateCriteriaGQL,
  InsertCriteriaInput,
  UpdateCriteriaGQL,
  UpdateCriteriaInput,
} from '@wb/core/schema/mutation-definitions/criteria.graphql';
import { BodyStylesGQL } from '@wb/core/schema/query-definitions/body-styles.graphql';
import { GetCriteriaGQL } from '@wb/core/schema/query-definitions/criteria.graphql';
import { FeesGQL } from '@wb/core/schema/query-definitions/fee.graphql';
import {
  MakesGQL,
  ModelYearsFromModelGQL,
  ModelsFromYearMakeGQL,
} from '@wb/core/schema/query-definitions/inventory.graphql';
import { AccessoriesGQL, ProductsGQL } from '@wb/core/schema/query-definitions/products.graphql';
import { AnalyticService } from '@wb/core/services/analytic.service';
import { FeatureService } from '@wb/core/services/feature.service';
import { RoutingService } from '@wb/core/services/routing.service';
import { Subject, merge } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import exclusionNamesStrings from './exclusion-names.json';

const TERMS_SEPARATOR = ',';

interface CriteriaData {
  type: 'accessory' | 'exclusion' | 'fee' | 'fi_add_on' | 'protection';
  uid?: string;
  accessoryUid?: String;
  feeUid?: String;
  fiAddOnUid?: String;
  bodyStyleUid?: String;
}

interface Option {
  code: string | null;
  name: string;
  price?: number;
}

@Component({
  selector: 'app-criteria-dialog',
  templateUrl: './criteria-dialog.component.html',
  styleUrls: ['./criteria-dialog.component.sass'],
})
export class CriteriaDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  public criteria?: Criteria;

  public saving: boolean = false;

  public loader: number = 0;

  public vehicleDataLoading: boolean = false;

  public modelsLoading: boolean = false;

  public criteriaType: string | undefined;

  forVehicleOptions: Option[] = [];

  forProductOptions: Option[] = [];

  fuelTypeOptions: Option[] = [];

  dealTypeOptions: Option[] = [];

  vehicleConditionOptions: Option[] = [];

  originOptions: Option[] = [];

  statesOptions: Option[] = [
    {
      name: this.translate.instant('addCriteria.criteria.state.placeholder'),
      code: null,
    },
    ...statesArray,
  ];

  operatorOptions: Option[] = [];

  makeOptions: Option[] = [];

  modelOptions: any[] = [];

  yearOptions: Option[] = [];

  bodyStyleOptions: Option[] = [];

  accessoryOptions: Option[] = [];

  feeOptions: Option[] = [];

  productOptions: Option[] = [];

  exclusionNames: string[] = exclusionNamesStrings;

  selectedYears: number[] = [];

  selectedMakeIds: number[] = [];

  formGroup: FormGroup = new FormGroup({
    type: new FormControl(null, Validators.required),
    accessoryUid: new FormControl({ value: this.data.accessoryUid ?? null, disabled: !!this.data.accessoryUid }),
    feeUid: new FormControl({ value: this.data.feeUid ?? null, disabled: !!this.data.feeUid }),
    bodyStyleUid: new FormControl({ value: this.data.bodyStyleUid ?? null, disabled: !!this.data.bodyStyleUid }),
    fiAddOnUid: new FormControl({ value: this.data.fiAddOnUid ?? null, disabled: !!this.data.fiAddOnUid }),
    for_vehicle: new FormControl(null, Validators.required),
    for_product: new FormControl(null),
    vehicle_condition: new FormControl([], Validators.required),
    deal_type: new FormControl(['finance', 'lease', 'cash'], Validators.required),
    origin: new FormControl(null, Validators.required),
    operator: new FormControl(''),
    city: new FormControl(''),
    county: new FormControl(''),
    state: new FormControl(''),
    fuel_type: new FormControl([], { updateOn: 'blur' }),
    input_name: new FormControl(''),
    input_name_normalized: new FormControl(''),
    vehicleBodyStyles: new FormControl([], { updateOn: 'blur' }),
    vehicleMakes: new FormControl([], { updateOn: 'blur' }),
    vehicleModels: new FormControl([]),
    vehicleYears: new FormControl([], { updateOn: 'blur' }),
    vehicleExclusions: new FormControl([]),
    productExclusions: new FormControl([]),
  });

  accountUid?: string;

  tooltipText: string = '';

  private dataReady$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CriteriaData,
    private readonly dialogRef: MatDialogRef<CriteriaDialogComponent>,
    private readonly snackBar: MatSnackBar,
    private readonly getCriteriaGQL: GetCriteriaGQL,
    private readonly updateCriteriaGQL: UpdateCriteriaGQL,
    private readonly createCriteriaGQL: CreateCriteriaGQL,
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly routing: RoutingService,
    private readonly makesGQL: MakesGQL,
    private readonly modelsFromYearMakeGQL: ModelsFromYearMakeGQL,
    private readonly modelYearsFromModelGQL: ModelYearsFromModelGQL,
    private readonly bodyStylesGQL: BodyStylesGQL,
    private readonly accessoriesGQL: AccessoriesGQL,
    private readonly feesGQL: FeesGQL,
    private readonly productsGQL: ProductsGQL,
    public readonly feature: FeatureService,
    private analytics: AnalyticService,
  ) {}

  async ngOnInit() {
    this.loader = 0;

    this.criteriaType = this.data.type;

    this.accountUid = await this.routing.getSelectedAccountUid(this.route);

    this.vehicleConditionOptions = this.translateOptions('addCriteria.criteria.vehicleCondition.', [
      'NEW_OR_USED_VEHICLE',
      'NEW_VEHICLE',
      'USED_VEHICLE',
    ]);

    this.dealTypeOptions = this.translateOptions('addCriteria.criteria.dealType.', ['finance', 'cash', 'lease']);

    this.originOptions = this.translateOptions('addCriteria.criteria.origin.', [
      'ANYWHERE',
      'ORIGINATING',
      'NOT_ORIGINATING',
    ]);

    this.fuelTypeOptions = this.translateOptions('addCriteria.criteria.fuelType.', [
      'diesel',
      'electric',
      'gas',
      'hybrid',
      'other',
    ]);

    this.forVehicleOptions = this.translateOptions('addCriteria.criteria.forVehicle.', ['all', 'specific']);

    this.forProductOptions = this.translateOptions('addCriteria.criteria.forProduct.', ['all', 'specific']);

    this.operatorOptions = this.translateOptions('addCriteria.criteria.operator.', ['EQUAL', 'NOT_EQUAL']);

    this.formGroup.patchValue({
      operator: this.operatorOptions[0].code,
      vehicle_condition: this.vehicleConditionOptions[0].code,
      origin: this.originOptions[0].code,
      fuel_type: [],
      for_vehicle: this.forVehicleOptions[0].code,
      for_product: this.forProductOptions[1].code,
    });

    Object.entries({
      ACCESSORIES: 'accessory',
      EXCLUSIONS: 'exclusion',
      FEES_TAXES: 'fee',
      FI: 'fi_add_on',
      PROTECTION: 'protection',
    }).find((value) => {
      if (this.data.type === value[1]) {
        this.formGroup.patchValue({
          type: value[0],
        });
      }
    });

    if (this.criteriaType === 'protection') {
      this.formGroup.controls.productExclusions.setValidators([Validators.required]);
      this.formGroup.controls.productExclusions.updateValueAndValidity();
    }

    this.formGroup
      .get('input_name')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value: string) => {
        if (value) {
          this.formGroup.patchValue({
            input_name_normalized: value.toUpperCase().replace(/[^a-zA-Z]/g, ''),
          });
        }
      });

    if (this.data.uid) {
      this.loader++;
      this.getCriteriaGQL
        .fetch({ uid: this.data.uid }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.loader--;
          this.criteria = response.data?.criteria?.criteria;
          this.formGroup.patchValue({
            ...this.criteria,
            for_vehicle: this.criteria.for_vehicle ?? 'all',
            fuel_type: this.criteria.fuel_type ? this.criteria.fuel_type?.split(TERMS_SEPARATOR) : [],
            deal_type: this.criteria.deal_type ? this.criteria.deal_type?.split(TERMS_SEPARATOR) : [],
            vehicleMakes: this.criteria.vehicleMakes?.map((one) => one.make_id.toString()) ?? [],
            vehicleModels: this.criteria.vehicleModels?.map((one) => one.model_id.toString()) ?? [],
            vehicleYears: this.criteria.vehicleYears?.map((one) => one.year.toString()) ?? [],
            vehicleBodyStyles: this.criteria.vehicleBodyStyles?.map((one) => one.bodyStyleUid) ?? [],
            productExclusions: this.criteria.productExclusions?.map((one) => one.exclusionUid) ?? [],
          });

          // Year/Make/Model section skeleton loads during models query so full criteria-dialog skeleton doesn't last as long
          this.modelsLoading =
            !!this.formGroup.controls.vehicleMakes?.value.length ||
            !!this.formGroup.controls.vehicleYears?.value.length;
          this.dataReady$.next();
        });
    }

    if (this.data.type === 'accessory') {
      this.loader++;
      this.accessoriesGQL
        .fetch({ limit: 0, where: { accountUid: this.accountUid } }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.accessoryOptions = response.data.accessories.accessories
            .filter((item) => !item.subaru_code)
            .map((one) => ({
              code: one.uid,
              name: one.title,
              price: one.price,
            }));
          this.loader--;
          this.dataReady$.next();
        });
    } else if (this.data.type === 'fee') {
      this.loader++;
      this.feesGQL
        .fetch({ where: { accountUid: this.accountUid! } }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.feeOptions = response.data.fees.fees.map((one) => ({
            code: one.uid!,
            name: one.title!,
          }));
          this.loader--;
        });
    } else if (this.data.type === 'fi_add_on' || this.data.type === 'protection') {
      this.loader++;
      this.productsGQL
        .fetch({ where: { accountUid: this.accountUid! } }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.productOptions = response.data.products.products.map((one) => ({
            code: one.uid,
            name: one.title,
            price: one.price,
          }));
          this.loader--;
          this.dataReady$.next();
        });
    }

    // This handles setting the selected fiAddOnUid as checked.
    // This happens when opening criteria from the products page.
    if (this.data.fiAddOnUid && this.data.type === 'protection') {
      this.formGroup.controls.productExclusions.patchValue([this.data.fiAddOnUid]);
    }

    // Make sure that the accessoryUid/fiAddOnUid and accessoryOptions/productOptions are not undefined, null or empty
    this.dataReady$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.criteria?.fiAddOnUid && this.productOptions.length > 0) {
        this.tooltipText = this.getTooltipText(this.productOptions, this.criteria?.fiAddOnUid);
      }
      if (this.criteria?.accessoryUid && this.accessoryOptions.length > 0) {
        this.tooltipText = this.getTooltipText(this.accessoryOptions, this.criteria?.accessoryUid);
      }
    });
  }

  ngAfterViewInit() {
    this.loadVehicleData();
  }

  public async loadVehicleData() {
    this.vehicleDataLoading = true;
    this.formGroup.controls.vehicleModels.disable();

    const bodyStyleRequest = new Promise((resolve) => {
      this.bodyStylesGQL
        .fetch({ limit: 0 }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.bodyStyleOptions = response.data.bodyStyles.bodyStyles
            .map((one) => ({
              code: one.uid!,
              name: one.body ?? '',
            }))
            .sort((a, b) => (a.name > b.name ? 1 : -1));
          resolve(true);
        });
    });

    const makesRequest = new Promise((resolve) => {
      this.makesGQL
        .fetch({ limit: 0 }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.makeOptions = response.data.makes.makes
            .map((one) => ({
              code: one.id.toString(),
              name: one.name,
            }))
            .sort((a, b) => (a.name > b.name ? 1 : -1));
          resolve(true);
        });
    });

    // Disable Model input and invalidate from while Quering models based on year/make data
    merge(this.formGroup.controls.vehicleMakes.valueChanges, this.formGroup.controls.vehicleYears.valueChanges)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async () => {
        this.modelsLoading = true;
        this.selectedYears = this.formGroup.controls.vehicleYears.value.map(Number) ?? [];
        this.selectedMakeIds = this.formGroup.controls.vehicleMakes.value.map(Number) ?? [];
        this.modelOptions = [];

        if (this.selectedYears?.length || this.selectedMakeIds?.length) {
          await this.updateModels();
        } else {
          // reset ymm info to default to reveal body_type/fuel_type fields again
          this.formGroup.patchValue({ vehicleModels: [], vehicleYears: [], vehicleMakes: [] }, { emitEvent: false });
          this.modelsLoading = false;
          this.formGroup.controls.vehicleModels.disable();
        }
      });

    this.yearOptions = this.generateYears().map((one) => ({
      code: one.toString(),
      name: one.toString(),
    }));

    await Promise.all([bodyStyleRequest, makesRequest]).then(() => (this.vehicleDataLoading = false));
  }

  async updateModels() {
    await new Promise((resolve) => {
      this.modelsFromYearMakeGQL
        .fetch(
          { where: { years: this.selectedYears, make_ids: this.selectedMakeIds }, limit: 0 },
          { fetchPolicy: 'network-only' },
        )
        .pipe(
          map((results: any) =>
            [...results.data.modelsFromYearMake.models]
              .sort((a: Model, b: Model) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()) || b.year - a.year)
              .map((result: Model) => {
                return {
                  code: result.id,
                  name:
                    !this.selectedYears.length && this.selectedMakeIds.length
                      ? result.name
                      : `${result.year} ${result.name}`,
                };
              }),
          ),
          take(1),
        )
        .subscribe({
          next: (response: any) => {
            this.modelOptions = response;
            this.validateModels();
            this.modelsLoading = false;
            this.formGroup.controls.vehicleModels.enable();
            resolve(true);
          },
          error: (err: HttpErrorResponse) => {
            console.error(err);
          },
        });
    });
  }

  // remove formGroup models as year and model selctions become more restrictive
  validateModels() {
    const models = this.formGroup.controls.vehicleModels.value;
    const validatedModels: string[] = [];
    this.formGroup.controls.vehicleModels.reset();
    if (models?.length) {
      for (const val of models) {
        if (this.modelOptions.find((x) => x.code === val)) {
          validatedModels.push(val);
        }
      }
    }
    this.formGroup.patchValue({ vehicleModels: validatedModels });
  }

  private generateYears(): number[] {
    const currentYear = new Date().getFullYear();

    const years: number[] = [];
    for (let i = currentYear + 1; i >= 1990; i--) {
      years.push(i);
    }
    return years;
  }

  private translateOptions(translationPrefix: string, list: string[]): Option[] {
    return list.map((one) => ({
      code: one,
      name: this.translate.instant(`${translationPrefix}${one}`),
    }));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSave(uid?: string) {
    // Allows disabled fields to be submitted
    this.formGroup.enable();
    this.formGroup.controls.vehicleModels.enable();
    if (!uid) {
      this.onCreate();
    } else {
      this.onUpdate();
    }
    this.formGroup.disable();
    this.analytics.track({ object: 'Account-Settings', action: 'Click', properties: { type: 'save_criteria' } });
  }

  // Form validation logic related to 'Specific Vehicles' selection
  get getSpecificOptionsValid() {
    return (
      this.formGroup.controls.fuel_type.value?.length ||
      this.formGroup.controls.vehicleMakes.value?.length ||
      this.formGroup.controls.vehicleYears.value?.length ||
      this.formGroup.controls.for_vehicle.value === 'all' ||
      this.formGroup.controls.vehicleBodyStyles.value?.length
    );
  }

  // Form validation logic related to 'Origin' selection
  get getOriginValid() {
    return (
      this.formGroup.controls.origin.value ||
      this.formGroup.controls.city.value ||
      this.formGroup.controls.county.value ||
      this.formGroup.controls.state.value
    );
  }

  // Form validation logic to ensure Accessory, Exclusion, Tax/Fee, or F&I is selected
  get typeSelectionValid() {
    switch (this.criteriaType) {
      case 'accessory':
        return !!this.formGroup.controls.accessoryUid.value;
      case 'exclusion':
        return !!this.formGroup.controls.input_name.value;
      case 'fee':
        return !!this.formGroup.controls.feeUid.value;
      case 'fi_add_on':
        return !!this.formGroup.controls.fiAddOnUid.value;
      case 'protection':
        return !!this.formGroup.controls.productExclusions.value;
    }
    return false;
  }

  async onCreate() {
    this.saving = true;

    const input: InsertCriteriaInput = await this.prepareInput({
      ...this.formGroup.value,
      accountUid: this.accountUid!,
    });

    this.createCriteriaGQL.mutate({ input }).subscribe({
      next: (response) => {
        this.data.uid = response.data?.createCriteria.criteria.uid;
        this.dialogRef.close(true);
        this.saving = false;
      },
      error: (error) => {
        this.snackBar.open(error, undefined, {
          duration: 3000,
        });
        this.saving = false;
      },
    });
  }

  async onUpdate() {
    this.saving = true;
    const input: UpdateCriteriaInput = await this.prepareInput(this.formGroup.value);
    this.updateCriteriaGQL.mutate({ uid: this.data.uid!, input }).subscribe({
      next: () => {
        this.dialogRef.close(true);
        this.saving = false;
      },
      error: (error) => {
        this.snackBar.open(error, undefined, {
          duration: 3000,
        });
        this.saving = false;
      },
    });
  }

  private async prepareInput(toSanitize: any) {
    let result = { ...toSanitize };
    Object.keys(result).forEach((key) => {
      if (result[key] == null) {
        delete result[key];
      }
    });

    if (result.origin === 'ANYWHERE') {
      result.city = null;
      result.county = null;
      result.state = null;
    }

    result.fuel_type = result.fuel_type?.length ? result.fuel_type?.join(TERMS_SEPARATOR) : null;

    result.deal_type = result.deal_type?.length ? result.deal_type?.join(TERMS_SEPARATOR) : null;

    result.vehicleBodyStyles = result.vehicleBodyStyles?.length
      ? result.vehicleBodyStyles.map((bodyStyleUid: string) => ({
          bodyStyleUid,
        }))
      : null;

    if (result.vehicleMakes) {
      result.vehicleMakes = result.vehicleMakes.map((makeIdString: string) => ({
        make_id: parseInt(makeIdString, 10),
      }));
    }

    if (result.vehicleModels) {
      result.vehicleModels = result.vehicleYears?.length
        ? (result.vehicleModels = result.vehicleModels.map((modelIdString: string) => ({
            model_id: parseInt(modelIdString, 10),
          })))
        : await this.queryModelsFromMakes(result.vehicleModels);
    }

    if (result.vehicleYears) {
      result.vehicleYears = result.vehicleYears.map((yearIdString: string) => ({
        year: parseInt(yearIdString, 10),
      }));
    }

    // TODO: This is a scaffold for when vehicle exclusions is implemented in the database.
    if (result.vehicleExclusions && result.vehicleExclusions.length > 0) {
      result.vehicleExclusions = [{ exclusion: result.vehicleExclusions }];
      // result.vehicleExclusions = result.vehicleExclusions.map((exclusion: string) => ({
      //   exclusion,
      // }));
    }

    if (result.productExclusions) {
      result.productExclusions = result.productExclusions.map((exclusionUid: string) => ({
        exclusionUid,
      }));
    }

    return result;
  }

  // Query all model years given a general list of models
  async queryModelsFromMakes(modelIds: string[]): Promise<any> {
    return new Promise((resolve) => {
      this.modelYearsFromModelGQL
        .fetch({ where: { model_ids: modelIds.map(Number) } })
        .pipe(
          take(1),
          map((models) => {
            return models.data.modelYearsFromModel.models.map((model) => {
              return { model_id: Number(model.id) };
            });
          }),
        )
        .subscribe({
          next: (models: any) => {
            resolve(models);
          },
          error: (err: HttpErrorResponse) => {
            resolve([]);
            console.error(err);
          },
        });
    });
  }

  onSelectionChange(event: any, isProduct: boolean) {
    const code = event.value;
    this.tooltipText = isProduct
      ? this.getTooltipText(this.productOptions, code)
      : this.getTooltipText(this.accessoryOptions, code);
  }

  private getTooltipText(options: Option[], code: String): string {
    const option = options.find((o: Option) => o.code === code);
    return `${option?.name} ($${option?.price})`;
  }
}
